import React, { useState, useEffect } from "react";
import { getDatabase, ref, get } from "firebase/database";
import { FaUsers, FaUser, FaShoppingCart, FaRupeeSign } from "react-icons/fa";
import AdminLayout from "../components/AdminLayout";
import "./AdminUsers.css";
import { setPageMeta } from "../utils/seo";

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("info");

  useEffect(() => {
    setPageMeta({
      title: "Admin Users | Banat India",
      description:
        "View and manage user accounts in the Banat India admin panel.",
      keywords: "admin users, Banat India admin, customer management",
    });
  }, []);

  const showToastMessage = (message, type = "info") => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
      setToastMessage("");
    }, 15000); // 15 seconds instead of 4
  };

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const database = getDatabase();
        const ordersRef = ref(database, "orders");
        
        const snapshot = await get(ordersRef);
        
        if (snapshot.exists()) {
          const ordersData = snapshot.val();
          const usersMap = new Map();

          // Process each user's orders to create user profiles
          Object.entries(ordersData).forEach(([userId, userOrders]) => {
            if (userOrders && typeof userOrders === 'object') {
              const userOrderList = Object.entries(userOrders).map(([orderKey, order]) => ({
                orderId: orderKey,
                userId: userId,
                ...order
              }));

              const totalSpent = userOrderList.reduce((sum, order) => sum + (parseFloat(order.total) || 0), 0);
              const activeOrders = userOrderList.filter(order => 
                order.status === 'paid' || order.status === 'pending'
              ).length;
              const lastOrder = userOrderList.length > 0 ? userOrderList[userOrderList.length - 1] : null;

              usersMap.set(userId, {
                userId,
                totalOrders: userOrderList.length,
                totalSpent,
                activeOrders,
                lastOrder,
                firstOrder: userOrderList.length > 0 ? userOrderList[0] : null,
                orderHistory: userOrderList
              });
            }
          });

          const usersList = Array.from(usersMap.values());
          setUsers(usersList);
        } else {
          createSampleUsers();
        }
        
        setLoading(false);
      } catch (error) {
        createSampleUsers();
        setLoading(false);
      }
    };

    const createSampleUsers = () => {
      const sampleUsers = [
        {
          userId: "user-001",
          totalOrders: 3,
          totalSpent: 2250.00,
          activeOrders: 1,
          lastOrder: {
            total: "750.00",
            createdAt: new Date().toISOString(),
            status: "paid"
          },
          firstOrder: {
            total: "500.00",
            createdAt: new Date(Date.now() - 86400000 * 30).toISOString(),
            status: "paid"
          },
          orderHistory: []
        },
        {
          userId: "user-002",
          totalOrders: 1,
          totalSpent: 1200.00,
          activeOrders: 0,
          lastOrder: {
            total: "1200.00",
            createdAt: new Date(Date.now() - 86400000 * 7).toISOString(),
            status: "paid"
          },
          firstOrder: {
            total: "1200.00",
            createdAt: new Date(Date.now() - 86400000 * 7).toISOString(),
            status: "paid"
          },
          orderHistory: []
        }
      ];
      setUsers(sampleUsers);
    };

    loadUsers();
  }, []);

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.userId.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === "all" || 
      (filterStatus === "active" && user.activeOrders > 0) ||
      (filterStatus === "inactive" && user.activeOrders === 0);
    
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (user) => {
    if (user.activeOrders > 0) {
      return <span className="status-badge active">Active</span>;
    } else {
      return <span className="status-badge inactive">Inactive</span>;
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="admin-loading">Loading users...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* Toast Notification */}
      {showToast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: toastType === 'info' ? '#17a2b8' : '#28a745',
          color: 'white',
          padding: '20px 25px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
          fontSize: '12px',
          fontWeight: 'normal',
          maxWidth: '600px',
          maxHeight: '80vh',
          overflowY: 'auto',
          wordWrap: 'break-word',
          whiteSpace: 'pre-wrap',
          animation: 'slideIn 0.3s ease-out',
          border: '2px solid rgba(255,255,255,0.2)'
        }}>
          {toastMessage}
        </div>
      )}
      
      <div className="admin-content-header">
        <h1>User Management</h1>
        <div className="admin-actions">
          <button className="export-btn" onClick={() => {
            const data = { users: users, exportDate: new Date().toISOString() };
            const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `admin-users-${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
          }}>
            📊 Export Users
          </button>
        </div>
      </div>

      <div className="users-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="filter-box">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Users</option>
            <option value="active">Active Users</option>
            <option value="inactive">Inactive Users</option>
          </select>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Users</h3>
          <p className="stat-number">{users.length}</p>
        </div>
        <div className="stat-card">
          <h3>Active Users</h3>
          <p className="stat-number">{users.filter(u => u.activeOrders > 0).length}</p>
        </div>
        <div className="stat-card">
          <h3>Total Revenue</h3>
          <p className="stat-number">₹{users.reduce((sum, user) => sum + user.totalSpent, 0).toFixed(2)}</p>
        </div>
        <div className="stat-card">
          <h3>Avg. Order Value</h3>
          <p className="stat-number">₹{(users.reduce((sum, user) => sum + user.totalSpent, 0) / users.reduce((sum, user) => sum + user.totalOrders, 0)).toFixed(2)}</p>
        </div>
      </div>

      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>User ID</th>
              <th>Status</th>
              <th>Total Orders</th>
              <th>Total Spent</th>
              <th>Active Orders</th>
              <th>Last Order</th>
              <th>First Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.userId}>
                <td>
                  <div className="user-info">
                    <FaUser className="user-icon" />
                    <span>{user.userId}</span>
                  </div>
                </td>
                <td>{getStatusBadge(user)}</td>
                <td>
                  <div className="orders-info">
                    <FaShoppingCart />
                    <span>{user.totalOrders}</span>
                  </div>
                </td>
                <td>
                  <div className="spent-info">
                    <FaRupeeSign />
                    <span>{user.totalSpent.toFixed(2)}</span>
                  </div>
                </td>
                <td>{user.activeOrders}</td>
                <td>
                  {user.lastOrder ? (
                    <div className="order-info">
                      <div>₹{user.lastOrder.total}</div>
                      <div className="order-date">
                        {new Date(user.lastOrder.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  ) : (
                    'No orders'
                  )}
                </td>
                <td>
                  {user.firstOrder ? (
                    <div className="order-info">
                      <div>₹{user.firstOrder.total}</div>
                      <div className="order-date">
                        {new Date(user.firstOrder.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  ) : (
                    'No orders'
                  )}
                </td>
                <td>
                  <button 
                    className="view-details-btn"
                    onClick={() => showToastMessage(`👤 User Details:\n${JSON.stringify(user, null, 2)}`, "info")}
                  >
                    👁️ View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredUsers.length === 0 && (
        <div className="no-users">
          <FaUsers />
          <p>No users found matching your criteria.</p>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminUsers; 